/* eslint-disable complexity, sonarjs/cognitive-complexity, react-hooks/exhaustive-deps */
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Element } from 'react-scroll';
import {
  BRAINTREE,
  PAYMENT_ELEMENT_NAME,
  PAYMENT_TYPES,
} from '../../../brands/default/constants';
import {
  ERROR_USING_PAYPAL_AND_WALLET,
  PAYMENT,
  PAYMENT_LOGIN_MESSAGE,
  PAYMENT_PASSENGER_DETAILS_MESSAGE,
} from '../../../brands/default/content/components/checkout';
import BraintreeService from '../../../helpers/braintreeService';
import ErrorTrackingService from '../../../helpers/errorTrackingService';
import {
  choosePaymentTypeTracking,
  getGateway,
  isApplePayEnabled,
  isGooglePayEnabled,
  showApplePay,
  showGooglePay,
  showPaypal,
} from '../../../helpers/payment';
import { paymentActionTypeHandler } from '../../../helpers/paymentErrorHandler';
import DisabledComponent from '../../_generic/DisabledComponent';
import Message from '../../_generic/Message';
import Spinner from '../../_generic/spinner/Spinner';
import Tile from '../../_generic/tile/Tile';
import TileBody from '../../_generic/tile/TileBody';
import useToast from '../../_generic/toast/UseToast';
import PaymentApple from './PaymentApple';
import PaymentCardBT from './PaymentCardBT';
import PaymentErrors from './PaymentErrors';
import PaymentGoogle from './PaymentGoogle';
import PaymentMessage from './PaymentMessage';
import PaymentPaypal from './PaymentPaypal';
import PaymentSavedCardBT from './PaymentSavedCardBt';
import PaymentTypeButtons from './PaymentTypeButtons';

const customError = (errors) => {
  console.error(errors);
};

const VipPaymentModule = ({
  deviceFingerprintId,
  failedExpressBuy = false,
  finalOrderAmount,
  orderCompleteHandler,
  orderFailedHandler,
  isAuthCheckFinished,
  isAuthenticated,
  orderSummary,
  paymentInstruments,
  basketProducts,
}) => {
  const toast = useToast();
  const [paymentType, setPaymentType] = useState(PAYMENT_TYPES.card);
  const [useSavedCard, setUseSavedCard] = useState(true);
  const [hasSavedCard, setHasSavedCard] = useState(false);
  const [creditCardOptions, setCreditCardOptions] = useState(null);
  const [isCardCheckDone, setIsCardCheckDone] = useState(false);
  const [errorObject, setErrorObject] = useState('');
  const gatewayValue = getGateway(paymentInstruments);
  const showBraintree = isAuthenticated && gatewayValue === BRAINTREE;
  const [isTravel, isTravelValid] = useSelector((state) => [
    state.checkout.isTravel,
    state.checkout.isTravelValid,
  ]);

  const switchToNewCard = () => {
    setUseSavedCard(false);
  };

  const switchToSavedCard = () => {
    setUseSavedCard(true);
  };

  const choosePaymentType = (type) => {
    setPaymentType(type);
    choosePaymentTypeTracking(type);
  };

  const handleOrderComplete = async (order) => {
    if (order) {
      orderCompleteHandler(true);
    }
  };

  const afterError = () => {
    orderFailedHandler(true);
  };

  const providerError = (error) => {
    setErrorObject(error);
  };

  const checkType = (type) => {
    switch (type) {
      case PAYMENT_TYPES.paypal:
        return showPaypal({
          finalOrderAmount,
          isAuthenticated,
          paymentInstruments,
        })
          ? type
          : PAYMENT_TYPES.card;
      case PAYMENT_TYPES.apple:
        return showApplePay({
          finalOrderAmount,
          isAuthenticated,
          paymentInstruments,
        }) && isApplePayEnabled()
          ? type
          : PAYMENT_TYPES.card;
      case PAYMENT_TYPES.google:
        return showGooglePay({
          finalOrderAmount,
          isAuthenticated,
          paymentInstruments,
        }) && isGooglePayEnabled()
          ? type
          : PAYMENT_TYPES.card;
      default:
        return type;
    }
  };

  const getSavedCards = async () => {
    try {
      return await BraintreeService.getVaultedCards();
    } catch (error) {
      const message = error.message;

      return toast.addToast(message, 'toast-error', 'top-right');
    }
  };

  useEffect(() => {
    BraintreeService.totalAmountToPay = finalOrderAmount;
  }, [finalOrderAmount]);

  useEffect(() => {
    BraintreeService.productOrderId = orderSummary.productOrderId;
    BraintreeService.dealVoucherProductId = orderSummary.dealVoucherProductId;
    BraintreeService.sendEmail = true;
  }, [orderSummary]);

  useEffect(() => {
    BraintreeService.deviceFingerprintId = deviceFingerprintId;
  }, [deviceFingerprintId]);

  useEffect(() => {
    if (!paymentInstruments && isAuthenticated) {
      ErrorTrackingService.logError({
        error: 'Empty paymentInstruments',
        message: 'Checkout is returning no payment methods for the user.',
      });
    }
    if (paymentInstruments) {
      if (showBraintree) {
        const setCards = async () => {
          const cards = await getSavedCards();
          if (cards?.length && !creditCardOptions) {
            setCreditCardOptions({ cards });
            setHasSavedCard(true);
            setIsCardCheckDone(true);
          }
        };
        setCards();
      }
      const defaultInstrument = find(paymentInstruments, { default: 'yes' });
      if (get(defaultInstrument, 'default', false)) {
        const newType =
          errorObject &&
          paymentActionTypeHandler({
            action: errorObject.action,
            paypalToken:
              find(paymentInstruments, {
                type: PAYMENT_TYPES.paypal,
              })?.durableToken === 'yes',
            savedCard: hasSavedCard,
            type: paymentType,
          });
        if (newType.type) {
          choosePaymentType(newType.type);
          if (newType.emptyForm) setUseSavedCard(false);
        } else {
          choosePaymentType(checkType(defaultInstrument.type));
        }
      }
    }
    setTimeout(() => {
      if (!isCardCheckDone) {
        setIsCardCheckDone(true);
      }
    }, 5_000);
  }, []);

  const setPaymentTypeToCard = useCallback(() => {
    if (paymentType === PAYMENT_TYPES.card) {
      return;
    }
    choosePaymentType(PAYMENT_TYPES.card);
  }, [paymentType]);

  useEffect(() => {
    if (finalOrderAmount === 0) {
      setPaymentTypeToCard();
    }
  }, [setPaymentTypeToCard, finalOrderAmount]);

  return (
    <>
      {failedExpressBuy && <PaymentMessage />}
      <Tile extraClasses="payment" title={PAYMENT}>
        <TileBody nobackground>
          <Element name={PAYMENT_ELEMENT_NAME} />
          <DisabledComponent isDisabled={!isAuthenticated} />
          <PaymentTypeButtons
            basketProducts={basketProducts}
            choosePaymentType={choosePaymentType}
            finalOrderAmount={finalOrderAmount}
            isAuthenticated={isAuthenticated}
            paymentInstruments={paymentInstruments}
            paymentType={paymentType}
          />
          {isTravel && !isTravelValid && (
            <Message
              extraClasses="passenger-details-to-purchase"
              message={PAYMENT_PASSENGER_DETAILS_MESSAGE}
            />
          )}
          {!isAuthenticated && (
            <Message
              extraClasses="login-to-purchase"
              message={PAYMENT_LOGIN_MESSAGE}
            />
          )}
          {isAuthenticated && !isCardCheckDone && <Spinner />}
          {errorObject.message && (
            <PaymentErrors message={errorObject.message} />
          )}
          {finalOrderAmount === 0 && (
            <Message message={ERROR_USING_PAYPAL_AND_WALLET} />
          )}
          {paymentType === PAYMENT_TYPES.card &&
            !isEmpty(paymentInstruments) &&
            isAuthCheckFinished &&
            isAuthenticated &&
            (!hasSavedCard || !useSavedCard) &&
            isCardCheckDone && (
              <PaymentCardBT
                afterError={afterError}
                atolProtected={false}
                basketProducts={basketProducts}
                finalOrderAmount={finalOrderAmount}
                hasSavedCard={hasSavedCard}
                isWalletApplied={false}
                orderComplete={handleOrderComplete}
                orderSummary={orderSummary}
                paymentInstruments={paymentInstruments}
                providerError={providerError}
                switchToSavedCard={switchToSavedCard}
                threeDSCheckEnabled
                totalQuantity={1}
                travelDisableButton={isTravel && !isTravelValid}
                vipPaywall
              />
            )}
          {paymentType === PAYMENT_TYPES.card &&
            !isEmpty(paymentInstruments) &&
            showBraintree &&
            hasSavedCard &&
            isCardCheckDone &&
            useSavedCard && (
              <PaymentSavedCardBT
                afterError={afterError}
                atolProtected={false}
                basketProducts={basketProducts}
                creditCardOptions={creditCardOptions}
                customError={customError}
                finalOrderAmount={finalOrderAmount}
                isWalletApplied={false}
                orderComplete={handleOrderComplete}
                orderSummary={orderSummary}
                paymentInstruments={paymentInstruments}
                providerError={providerError}
                switchToNewCard={switchToNewCard}
                threeDSCheckEnabled
                totalQuantity={1}
                travelDisableButton={isTravel && !isTravelValid}
                vipPaywall
              />
            )}
          {paymentType === PAYMENT_TYPES.paypal && (
            <PaymentPaypal
              afterError={afterError}
              atolProtected={false}
              basketProducts={basketProducts}
              customError={customError}
              finalOrderAmount={finalOrderAmount}
              isWalletApplied={false}
              orderComplete={handleOrderComplete}
              orderSummary={orderSummary}
              providerError={providerError}
              totalQuantity={1}
              travelDisableButton={isTravel && !isTravelValid}
              vipPaywall
            />
          )}
          {paymentType === PAYMENT_TYPES.google && isGooglePayEnabled() && (
            <PaymentGoogle
              afterError={afterError}
              atolProtected={false}
              basketProducts={basketProducts}
              customError={customError}
              finalOrderAmount={finalOrderAmount}
              isWalletApplied={false}
              orderComplete={handleOrderComplete}
              orderSummary={orderSummary}
              providerError={providerError}
              totalQuantity={1}
              travelDisableButton={isTravel && !isTravelValid}
              vipPaywall
            />
          )}
          {paymentType === PAYMENT_TYPES.apple && isApplePayEnabled() && (
            <PaymentApple
              afterError={afterError}
              atolProtected={false}
              basketProducts={basketProducts}
              customError={customError}
              finalOrderAmount={finalOrderAmount}
              isWalletApplied={false}
              orderComplete={handleOrderComplete}
              orderSummary={orderSummary}
              providerError={providerError}
              totalQuantity={1}
              travelDisableButton={isTravel && !isTravelValid}
              vipPaywall
            />
          )}
        </TileBody>
      </Tile>
    </>
  );
};

VipPaymentModule.propTypes = {
  basketProducts: PropTypes.array,
  deviceFingerprintId: PropTypes.string,
  failedExpressBuy: PropTypes.bool,
  finalOrderAmount: PropTypes.number,
  isAuthCheckFinished: PropTypes.bool,
  isAuthenticated: PropTypes.bool.isRequired,
  orderCompleteHandler: PropTypes.func,
  orderFailedHandler: PropTypes.func,
  orderSummary: PropTypes.object.isRequired,
  paymentInstruments: PropTypes.any,
};

export default VipPaymentModule;
